import { API_BASE_URL } from '@synergy/config';
import { AlertCallSignIcon } from '@synergy/theme-next';
import {
    BonusSettlementStatus,
    BonusSettlementSupplierStatus,
    BonusSettlementType,
    getBonusSettlementSupplierTranslationId,
} from '@synergy/models';
import { Box, Stack, Tooltip } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { PaloAmount, PaloColumn, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolConfirmationModal, PoolDocumentTitle, PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { AdditionalInfoColumn } from '../components/additional-info-column';
import { BonusSettlementSupplier } from '../../models/bonus-settlement-supplier-overview';
import { FilesTooltip } from '../components/files-tooltip';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementsSupplierOverviewDetailsSelector,
    bonusSettlementsSupplierOverviewRequestingSelector,
    bonusSettlementsSupplierOverviewSelector,
} from '../store/bonus-settlements-supplier-overview-selector';
import {
    completeSuppliers,
    deleteSupplier,
    fetchBonusSettlementsSupplierOverview,
    fetchBonusSettlementsSupplierOverviewDetails,
    reclaimSupplier,
    remindSuppliers,
    reopenSupplier,
    revertReclaimSupplier,
} from '../store/bonus-settlements-supplier-overview-actions';
import { defaultSearchParamsSelector } from '../../../../../store/default-searchparams-selector';
import { getBonusClassificationTranslationId } from '../../models/get-bonus-classification-translation-id';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { usePaloListViews } from '../../../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const PaloBonusSettlementSupplierList = (): ReactElement | null => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns } = usePaloListViews('bonus-settlement-suppliers');
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const { id: bonusSettlementId } = useParams<{ id: string }>();

    const suppliers = useAppSelector(bonusSettlementsSupplierOverviewSelector);
    const requesting = useAppSelector(bonusSettlementsSupplierOverviewRequestingSelector);
    const details = useAppSelector(bonusSettlementsSupplierOverviewDetailsSelector);
    const currency = useAppSelector(state => state.settings.currency);
    const downloadBaseUrl = useAppSelector(state => state.settings.privateFilesystemPath);

    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams);
    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? 50);

    const [remindSupplier, setRemindSupplier] = useState<BonusSettlementSupplier | null>(null);
    const [completeSupplier, setCompleteSupplier] = useState<BonusSettlementSupplier | null>(null);
    const [toDeleteSupplier, setToDeleteSupplier] = useState<BonusSettlementSupplier | null>(null);

    useEffect(() => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(fetchBonusSettlementsSupplierOverview({ id: +bonusSettlementId, searchParams }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, searchParams]);

    // The totals belong to the settlement, not to the page of it on screen, so
    // they are fetched once rather than with every filter and every page turn.
    useEffect(() => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(fetchBonusSettlementsSupplierOverviewDetails(+bonusSettlementId));
        }
    }, [dispatch, bonusSettlementId]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const reload = (): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(fetchBonusSettlementsSupplierOverview({ id: +bonusSettlementId, searchParams }));
            dispatch(fetchBonusSettlementsSupplierOverviewDetails(+bonusSettlementId));
        }
    };

    const onRemindSupplier = (supplier: BonusSettlementSupplier): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(
                remindSuppliers({
                    bonusSettlementId: +bonusSettlementId,
                    remindAll: false,
                    bonusSettlementSupplierIds: [supplier.id],
                    supplierName: supplier.supplierName,
                })
            );
        }
        setRemindSupplier(null);
    };

    const onCompleteSupplier = async (supplier: BonusSettlementSupplier): Promise<void> => {
        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(
                completeSuppliers({
                    bonusSettlementId: +bonusSettlementId,
                    completeAll: false,
                    bonusSettlementSupplierIds: [supplier.id],
                    supplierName: supplier.supplierName,
                })
            );
            reload();
        }
        setCompleteSupplier(null);
    };

    const onDeleteSupplier = (supplierId: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(deleteSupplier({ bonusSettlementId: +bonusSettlementId, bonusSettlementSupplierId: supplierId }));
        }
        setToDeleteSupplier(null);
    };

    const onReclaimSupplier = (supplierId: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(reclaimSupplier({ bonusSettlementId: +bonusSettlementId, bonusSettlementSupplierId: supplierId }));
        }
    };

    const onRevertReclaimSupplier = (supplierId: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(
                revertReclaimSupplier({ bonusSettlementId: +bonusSettlementId, bonusSettlementSupplierId: supplierId })
            );
        }
    };

    const onReopenSupplier = async (supplierId: number): Promise<void> => {
        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(
                reopenSupplier({ bonusSettlementId: +bonusSettlementId, bonusSettlementSupplierId: supplierId })
            );
            dispatch(fetchBonusSettlementsSupplierOverviewDetails(+bonusSettlementId));
        }
    };

    const onExportSupplier = (supplierId: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            window.open(
                urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/suppliers/${supplierId}/export`),
                '_self'
            );
        }
    };

    const openDetail = (supplier: BonusSettlementSupplier): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            navigate(
                ROUTES.bonusSettlements.supplierDetail
                    .replace(':bonusSettlementId', bonusSettlementId)
                    .replace(':id', supplier.id.toString())
            );
        }
    };

    const columns: PaloColumn<BonusSettlementSupplier>[] = [
        {
            key: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: supplier => (
                <Stack direction="row" alignItems="center" gap={1}>
                    <PaloStatusPill
                        status={supplier.status}
                        label={<FormattedMessage id={getBonusSettlementSupplierTranslationId(supplier.status)} />}
                    />
                    {supplier.isReclaimed && (
                        <Tooltip title={<FormattedMessage id="app.bonusSettlement.reclaim.toolTip" />}>
                            <Box sx={{ display: 'flex' }}>
                                <AlertCallSignIcon color="error" />
                            </Box>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
        {
            key: 'supplier',
            head: <FormattedMessage id="app.table.column.supplierWithId" />,
            cell: supplier => supplier.supplierName,
        },
        {
            key: 'supplierNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            variant: 'ref',
            cell: supplier => supplier.supplierId,
        },
        {
            key: 'city',
            head: <FormattedMessage id="app.table.column.place" />,
            cell: supplier => supplier.city,
        },
        {
            key: 'bonusClassification',
            head: <FormattedMessage id="app.table.column.bonusClassification" />,
            cell: supplier =>
                supplier.bonusClassification && (
                    <FormattedMessage id={getBonusClassificationTranslationId(supplier.bonusClassification)} />
                ),
        },
        {
            key: 'calculatedBonus',
            head: <FormattedMessage id="app.table.column.bonusCalculated" />,
            type: 'amount',
            currency,
            cell: supplier => <PaloAmount value={supplier.calculatedBonus} />,
        },
        {
            key: 'actualBonus',
            head: <FormattedMessage id="app.table.column.bonusActual" />,
            type: 'amount',
            currency,
            cell: supplier => <PaloAmount value={supplier.actualBonus} />,
        },
        {
            key: 'additionalInformation',
            head: <FormattedMessage id="app.table.column.additionalInformation" />,
            cell: supplier => (
                <AdditionalInfoColumn
                    bonusConditions={supplier.bonusConditions}
                    internalNote={supplier.internalNote}
                    noteFromSupplier={supplier.noteFromSupplier}
                />
            ),
        },
        {
            key: 'lastReminderAt',
            head: <FormattedMessage id="app.table.column.lastReminder" />,
            variant: 'date',
            cell: supplier =>
                supplier.lastReminderAt ? (
                    <FormattedDate value={supplier.lastReminderAt} year="numeric" month="short" day="numeric" />
                ) : (
                    '–'
                ),
        },
        {
            key: 'files',
            head: <FormattedMessage id="app.common.files" />,
            cell: supplier => <FilesTooltip files={supplier.documents} downloadBaseUrl={downloadBaseUrl} />,
        },
    ];

    const rowActions = (supplier: BonusSettlementSupplier): PaloRowAction[] => {
        const actions: PaloRowAction[] = [];

        if (
            [BonusSettlementSupplierStatus.Open, BonusSettlementSupplierStatus.PartiallySent].includes(
                supplier.status
            ) &&
            details?.status === BonusSettlementStatus.Sent &&
            details.type === BonusSettlementType.Main
        ) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.remindSupplier" />,
                onClick: () => setRemindSupplier(supplier),
            });
        }

        if (
            [
                BonusSettlementSupplierStatus.Sent,
                BonusSettlementSupplierStatus.AutomaticallySent,
                BonusSettlementSupplierStatus.Reclaimed,
                BonusSettlementSupplierStatus.Completed,
            ].includes(supplier.status) &&
            details?.status !== BonusSettlementStatus.Completed
        ) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.reopen" />,
                onClick: () => onReopenSupplier(supplier.id),
                disabled: details?.hasCompletedCustomers,
                hint: details?.hasCompletedCustomers && (
                    <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.hastCompletedCustomers" />
                ),
            });
        }

        if (!supplier.isReclaimed && supplier.status === BonusSettlementSupplierStatus.Completed) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.reclaim" />,
                onClick: () => onReclaimSupplier(supplier.id),
            });
        }

        if (supplier.isReclaimed && details?.status === BonusSettlementStatus.Completed) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.revertReclaim" />,
                onClick: () => onRevertReclaimSupplier(supplier.id),
            });
        }

        if (
            [BonusSettlementSupplierStatus.Sent, BonusSettlementSupplierStatus.AutomaticallySent].includes(
                supplier.status
            ) &&
            details?.status !== BonusSettlementStatus.Draft
        ) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.complete" />,
                onClick: () => setCompleteSupplier(supplier),
            });
        }

        actions.push({
            label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.export" />,
            onClick: () => onExportSupplier(supplier.id),
        });

        if (supplier.status === BonusSettlementSupplierStatus.Open) {
            actions.push({
                label: (
                    <PoolFormattedMessage
                        id="app.bonusSettlementsSupplierOverview.action.remove"
                        values={{ supplier: supplier.supplierName }}
                    />
                ),
                onClick: () => setToDeleteSupplier(supplier),
            });
        }

        return actions;
    };

    if (!details) {
        return null;
    }

    return (
        <>
            <PoolDocumentTitle
                titleTranslationId="app.navigation.bonusSettlements"
                name={intl.formatMessage({ id: 'app.bonusSettlements.tab.supplier' })}
            />
            <PaloListView<BonusSettlementSupplier>
                embedded={true}
                columns={orderColumns(columns)}
                onMoveColumn={moveColumn}
                hiddenColumnKeys={hiddenColumnKeys}
                shownColumnKeys={shownColumnKeys}
                onToggleColumn={toggleColumn}
                items={suppliers?.items ?? []}
                getRowId={supplier => supplier.id}
                requesting={requesting}
                emptyLabel="Keine Lieferanten in dieser Abrechnung."
                onRowClick={openDetail}
                rowActions={rowActions}
                pagination={{
                    count: suppliers?.total ?? 0,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                <PaloNextThemeProvider>
                    {/* eslint-disable @typescript-eslint/no-non-null-assertion */}
                    <PoolConfirmationModal
                        open={!!toDeleteSupplier}
                        title={
                            <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.removeSupplier.title" />
                        }
                        onCancel={() => setToDeleteSupplier(null)}
                        onConfirm={() => onDeleteSupplier(toDeleteSupplier!.id)}
                        action={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.remove" />}
                    >
                        <PoolFormattedMessage
                            id="app.bonusSettlementsSupplierOverview.action.removeSupplier.text"
                            values={{ supplier: toDeleteSupplier?.supplierName }}
                        />
                    </PoolConfirmationModal>

                    <PoolConfirmationModal
                        open={!!remindSupplier}
                        isSuccessDialog={true}
                        title={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.remindSupplier" />}
                        onCancel={() => setRemindSupplier(null)}
                        onConfirm={() => onRemindSupplier(remindSupplier!)}
                        action={<FormattedMessage id="app.common.remind" />}
                    >
                        <PoolFormattedMessage
                            id="app.bonusSettlementsSupplierOverview.action.remindSupplier.text"
                            values={{ supplier: remindSupplier?.supplierName }}
                        />
                    </PoolConfirmationModal>

                    <PoolConfirmationModal
                        open={!!completeSupplier}
                        isSuccessDialog={true}
                        title={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.complete" />}
                        onCancel={() => setCompleteSupplier(null)}
                        onConfirm={() => onCompleteSupplier(completeSupplier!)}
                        action={<FormattedMessage id="app.common.complete" />}
                    >
                        <PoolFormattedMessage
                            id="app.bonusSettlementsSupplierOverview.action.completeSupplier.text"
                            values={{ supplier: completeSupplier?.supplierName }}
                        />
                    </PoolConfirmationModal>
                </PaloNextThemeProvider>
            </PaloListView>
        </>
    );
};
